import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Typography,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Grid,
  LinearProgress,
  Divider,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Button,
} from "@mui/material";

import GroupIcon from "@mui/icons-material/Group";
import PanToolIcon from "@mui/icons-material/PanTool";
import PersonIcon from "@mui/icons-material/Person";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BoltIcon from "@mui/icons-material/Bolt";
import ApiMap from "../../utils/ApiMap";
import Moment from "react-moment";
import "moment-timezone";
import GameServer from "./GameServer";
import AuthService from "../../utils/AuthService";
import axios from "axios";

/* ----------------------------------------------------------------
   Design tokens
---------------------------------------------------------------- */
const fontStack =
  '"Inter","DM Sans","Poppins",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol",sans-serif';

const chipTextSx = {
  ".MuiChip-label": {
    fontWeight: 600,
    letterSpacing: ".01em",
    fontFamily: fontStack,
  },
};

const iconBadgeSx = (t) => ({
  p: 0.6,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  mr: 0.75,
  ...(t.palette.mode === "dark"
    ? {
        bgcolor: "rgba(99,102,241,0.18)", // indigo glassy tone
        color: "#A5B4FC", // soft indigo
      }
    : {
        bgcolor: "rgba(20,184,166,0.1)", // teal tint
        color: "#0D9488",
      }),
  boxShadow:
    t.palette.mode === "dark"
      ? "0 0 8px rgba(99,102,241,0.25)"
      : "0 0 6px rgba(20,184,166,0.2)",
});

const surfaceSx = (t) =>
  t.palette.mode === "dark"
    ? {
        // 🌙 Glassy but readable in dark mode
        bgcolor: "rgba(30, 41, 59, 0.85)", // brighter navy tint
        border: "1px solid rgba(148, 163, 184, 0.25)",
        boxShadow:
          "0 0 20px rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.5)",
        backdropFilter: "blur(12px)",
      }
    : {
        // ☀️ Bright, clean glass surface
        bgcolor: "rgba(255, 255, 255, 0.96)", // whiter tone
        border: "1px solid rgba(0,0,0,0.05)",
        boxShadow: "0 0 20px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)",
        backdropFilter: "blur(10px)",
      };

const gradientCardSx = (t) =>
  t.palette.mode === "dark"
    ? {
        background:
          "linear-gradient(135deg, rgba(79,70,229,0.28) 0%, rgba(20,184,166,0.18) 100%)",
      }
    : {
        background:
          "linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(45,212,191,0.12) 100%)",
      };

const glowShadow = (t) =>
  t.palette.mode === "dark"
    ? "0 0 0 1px rgba(45,212,191,0.3), 0 10px 30px rgba(79,70,229,0.25)"
    : "0 0 0 1px rgba(99,102,241,0.15), 0 10px 30px rgba(20,184,166,0.18)";

/* ----------------------------------------------------------------
   Helpers
---------------------------------------------------------------- */
const coerceNumber = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^0-9.+-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
const isTruthy = (v) => v === true || v === "true" || v === 1 || v === "1";

// const fmtSecs = (secs) =>
//   secs >= 60 ? `${(secs / 60).toFixed(1)} min` : `${secs.toFixed(1)}s`;
// const fmtMs = (ms) => {
//   const s = ms / 1000;
//   return s >= 60 ? `${(s / 60).toFixed(1)} min` : `${s.toFixed(1)}s`;
// };

/* ----------------------------------------------------------------
   Circular Timer (MUI CircularProgress)
---------------------------------------------------------------- */
function TimerRing({ startedon, duration, onTimesUp }) {
  // Hooks must be top-level (ESLint-safe)
  const theme = useTheme();
  const [now, setNow] = useState(Date.now());
  const [fired, setFired] = useState(false);

  // Smooth tick
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const valid = startedon && duration;
  // Compute timing (even if invalid we avoid conditional hooks)
  const startMs = valid ? new Date(startedon).getTime() : 0;
  const totalMs = valid ? duration * 60 * 1000 : 0;
  const endMs = startMs + totalMs;
  const remaining = Math.max(0, endMs - now);
  const pct = totalMs > 0 ? remaining / totalMs : 0;

  // Fire onTimesUp once
  useEffect(() => {
    if (valid && !fired && remaining <= 0) {
      setFired(true);
      if (typeof onTimesUp === "function") onTimesUp();
    }
  }, [valid, remaining, onTimesUp, fired]);

  if (!valid) return null;

  const totalSeconds = Math.ceil(remaining / 1000);
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  const label = `${String(min).padStart(2, "0")}:${String(sec).padStart(
    2,
    "0"
  )}`;

  const size = 84;
  const thickness = 4.6;
  const trackColor =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(15,23,42,0.12)";

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "grid",
        placeItems: "center",
      }}
      role="timer"
      aria-label="Countdown timer"
    >
      {/* Track */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          color: trackColor,
          position: "absolute",
          left: 0,
          top: 0,
        }}
      />
      {/* Progress */}
      <CircularProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, pct * 100))}
        size={size}
        thickness={thickness}
        sx={{
          color:
            theme.palette.mode === "dark"
              ? "#5EEAD4" // teal glow
              : "#6366F1", // indigo
          position: "absolute",
          left: 0,
          top: 0,
          filter:
            theme.palette.mode === "dark"
              ? "drop-shadow(0 0 6px rgba(94,234,212,0.35))"
              : "drop-shadow(0 0 6px rgba(99,102,241,0.35))",
          transition: "color .25s ease",
        }}
      />
      {/* Label */}
      <Typography
        variant="subtitle2"
        sx={{
          position: "absolute",
          fontWeight: 800,
          letterSpacing: ".02em",
          fontFamily: fontStack,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const Stat = ({ label, value, glow = false }) => (
  <Box
    sx={(t) => ({
      p: 1.25,
      borderRadius: 1.5,
      minWidth: 140,
      bgcolor:
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
      border:
        t.palette.mode === "dark"
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(15,23,42,0.08)",
      boxShadow: glow ? glowShadow(t) : "none",
    })}
  >
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        fontWeight: 600,
        lineHeight: 1.25,
        fontFamily: fontStack,
      }}
    >
      {label}
    </Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, lineHeight: 1.25, fontFamily: fontStack }}
      >
        {value}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

const StatusChip = ({ player }) => {
  if (player?.IsOffline) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="error"
        icon={<PowerSettingsNewIcon fontSize="small" />}
        label="Offline"
        sx={chipTextSx}
      />
    );
  }
  if (isTruthy(player?.IsWaiting) || isTruthy(player?.InWaitingRoomFlag)) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="warning"
        icon={<PanToolIcon fontSize="small" />}
        label="Needs approval"
        sx={chipTextSx}
      />
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      color="primary"
      icon={<PersonIcon fontSize="small" />}
      label="Playing"
      sx={chipTextSx}
    />
  );
};

function EmptyState({ title = "Waiting for players to join…", hint }) {
  return (
    <Box
      sx={(t) => ({
        p: 4,
        textAlign: "center",
        borderRadius: 2,
        ...surfaceSx(t),
      })}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          letterSpacing: ".01em",
          fontFamily: fontStack,
        }}
      >
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {hint || "Invite learners with the PIN above to get started."}
      </Typography>
    </Box>
  );
}

/* ----------------------------------------------------------------
   Main component
---------------------------------------------------------------- */
export default function PlayerTable({
  duration,
  players = [],
  waitingPlayers = [],
  offlinePlayers = [],
  startedon,
  gamename,
  modulename,
  courseid,
  totalQuestions,
  totalquestions,
  gamecode,
  activityLog,
  onPlayerRemoved,
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [itemAttempts, setItemAttempts] = useState([]);
  //const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItemCid, setPreviewItemCid] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timesUp, setTimesUp] = useState(false);
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);

  const questionCount = coerceNumber(totalQuestions ?? totalquestions);
  const isInstructor =
    AuthService.isFaculty(courseid) || AuthService.isCourseAdmin();
  console.log(activityLog);
  const navigate = useNavigate();
  useEffect(() => {
    if (!gamecode) return; // nothing to load yet

    setIsLoading(true);
    setError(null);
    setQuizData(null);

    const url = `${ApiMap.GAME_ASSESSMENT}/${gamecode}/assessment`;

    const fetchData = async () => {
      try {
        const resp = await axios.get(url, AuthService.getAuthHeader());
        setQuizData(resp.data);
      } catch (err) {
        console.error("❌ Failed to fetch quiz:", err);
        setError("Failed to load quiz. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [gamecode]);

  // Computed
  const getProgressValue = (p) => {
    if (!p) return 0;

    const coerceNumber = (v) => {
      if (v == null || v === "") return null;
      if (typeof v === "number") return Number.isFinite(v) ? v : null;
      if (typeof v === "string") {
        const n = Number(v.replace(/[^0-9.+-]/g, ""));
        return Number.isFinite(n) ? n : null;
      }
      return null;
    };

    // 1️⃣ Try to use PercentCompleted only if > 0 (ignore 0%)
    const percent = coerceNumber(
      p.PercentCompleted ?? p.percentCompleted ?? p.percent ?? null
    );
    if (percent != null && percent > 0) {
      return Math.min(Math.max(percent, 0), 100);
    }

    // 2️⃣ Prefer Score-based progress (since backend gives Score + ItemCount)
    const total = coerceNumber(
      p.ItemCount ?? p.ItemsTotal ?? p.TotalItems ?? p.Total ?? questionCount
    );
    const score = coerceNumber(p.Score ?? p.Points ?? p.score ?? p.points);

    if (score != null && total != null && total > 0) {
      const progress = (score / total) * 100;
      return Math.min(100, Math.max(0, progress));
    }

    // 3️⃣ Fallback to ItemsCompleted (for legacy data)
    const done = coerceNumber(
      p.ItemsCompleted ??
        p.CompletedItems ??
        p.Completed ??
        p.CurrentItem ??
        null
    );
    if (total != null && total > 0 && done != null) {
      const progress = (done / total) * 100;
      return Math.min(100, Math.max(0, progress));
    }

    return 0;
  };

  const sortedPlayers = useMemo(
    () =>
      [...players].sort((a, b) => getProgressValue(b) - getProgressValue(a)),
    [players, getProgressValue]
  );

  const isWaitingPlayer = (p) =>
    isTruthy(p?.IsWaiting) || isTruthy(p?.InWaitingRoomFlag);

  const activePlayers = useMemo(
    () => sortedPlayers.filter((p) => !isWaitingPlayer(p) && !p?.IsOffline),
    [sortedPlayers]
  );

  const leaderboardPlayers = useMemo(
    () => activePlayers.slice(0, 5),
    [activePlayers]
  );

  const initials = (p) =>
    `${(p?.FirstName || p?.Name || "?").charAt(0)}${(p?.LastName || "").charAt(
      0
    )}`.trim() || "?";

  const itemsSummary = (p) => {
    const total =
      coerceNumber(
        p.ItemCount ?? p.ItemsTotal ?? p.TotalItems ?? p.Total ?? questionCount
      ) ?? 0;

    // ✅ Use Score as completed if ItemsCompleted isn't tracked
    const completed =
      coerceNumber(
        p.Score ??
          p.ItemsCompleted ??
          p.CompletedItems ??
          p.Completed ??
          p.CurrentItem
      ) ?? 0;

    if (total > 0) return `${completed}/${total}`;
    return completed ? `${completed} completed` : "—";
  };

  const formatProgressLabel = (p) => {
    const coerceNumber = (v) => {
      if (v == null || v === "") return null;
      if (typeof v === "number") return Number.isFinite(v) ? v : null;
      if (typeof v === "string") {
        const n = Number(v.replace(/[^0-9.+-]/g, ""));
        return Number.isFinite(n) ? n : null;
      }
      return null;
    };

    // 1️⃣ Try percent only if > 0 (ignore 0%)
    const percent = coerceNumber(
      p.PercentCompleted ??
        p.percentCompleted ??
        p.percentcompleted ??
        p.percent ??
        null
    );
    if (percent != null && percent > 0) return `${Math.round(percent)}%`;

    // 2️⃣ Use Score + ItemCount (your real backend fields)
    const total =
      coerceNumber(
        p.ItemCount ?? p.ItemsTotal ?? p.TotalItems ?? p.Total ?? questionCount
      ) ?? 0;
    const score = coerceNumber(p.Score ?? p.Points ?? null) ?? 0;

    if (total > 0) {
      const percentFromScore = (score / total) * 100;
      return `${Math.round(percentFromScore)}%`;
    }

    // 3️⃣ Fallback to completed items (legacy)
    const done = coerceNumber(
      p.ItemsCompleted ?? p.CompletedItems ?? p.Completed ?? p.CurrentItem
    );
    if (total && done != null) return `${done}/${total}`;

    return "—";
  };

  function getAvgTime(p) {
    console.log(p);
    // Extract from possible backend fields
    let t =
      p.averageResponseMs ??
      p.avgRespTimeInMillsec ??
      p.AvgRespTimeInMillsec ??
      p.responseTimeInMillsec ??
      null;

    let ms = Number(t);
    if (!Number.isFinite(ms) || ms <= 0) return "—";

    // --- Auto-normalize scientific notation / tiny fractions ---
    if (ms < 1e-9) ms *= 1e12; // e.g. 1e-31 → 1e-19 ms
    else if (ms < 1e-6) ms *= 1e9; // e.g. 1e-12 → 1e-3 ms
    else if (ms < 1e-3) ms *= 1e6; // e.g. 0.00004 → 40 ms
    else if (ms < 1) ms *= 1e3; // e.g. 0.4 → 400 ms

    // --- Clip absurdly huge numbers (safety: max 1 h) ---
    if (ms > 3.6e6) ms = 3.6e6;

    // --- Friendly display formatting ---
    if (ms < 1) return "< 1 ms";
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
    return `${ms.toFixed(0)} ms`;
  }

  const lastActive = (p) =>
    p?.LastReportedOn ?? p?.LastSeen ?? p?.UpdatedOn ?? p?.JoinedOn ?? null;

  // Actions
  const sendAdmitStudent = (p) => {
    const payload = {
      Code: gamecode,
      PlayerId: p.MemberId || p.PlayerId || p.UserId || p.Email,
      ActivityId: p.ActivityId,
    };
    GameServer.notify(gamecode, "msgteacheradmitplayer", payload, {
      msgdisplayactivity: (data) => console.log("✅ Admit acknowledged:", data),
      error: (e) => console.error("❌ admit error:", e),
    });
  };

  const sendRemoveStudent = (p) => {
    const payload = {
      Code: gamecode,
      PlayerId: p.MemberId || p.PlayerId || p.UserId || p.Email,
      ActivityId: p.ActivityId,
    };

    GameServer.notify(gamecode, "msgremovestudent", payload, {
      msgdisplayactivity: (data) => {
        console.log("✅ Remove acknowledged:", data);
        // ✅ Optimistically remove from UI
        if (typeof onPlayerRemoved === "function") {
          onPlayerRemoved(p);
        }
      },
      error: (e) => console.error("❌ remove error:", e),
    });
  };

  // Drawer / Preview handlers
  const openDetails = (player) => {
    console.log("🧩 Opening details for:", player);

    // 🔍 Match player with activityLog entries using PK
    const userPk = `user#${player.UserId}`;
    const attemptsForPlayer = Array.isArray(activityLog)
      ? activityLog.filter((entry) => entry?.PK === userPk)
      : [];

    // 🧠 Normalize each attempt to a consistent shape
    const normalizedAttempts = attemptsForPlayer.map((entry, index) => {
      // Extract itemId cleanly from SK
      const itemIdMatch =
        entry.SK?.match(/itemId#([^;]+)/i) ||
        entry.ItemId ||
        entry.itemId ||
        null;
      const itemId = itemIdMatch?.[1] ?? itemIdMatch ?? `Item ${index + 1}`;

      return {
        itemId,
        itemKey: entry.SK ?? null,
        attempts: entry.attempts ?? entry.Attempt ?? entry.Attempts ?? 0,
        avgResponseMs:
          entry.avgRespTimeInMillsec ??
          entry.AvgRespTimeInMillsec ??
          entry.responseTimeInMillsec ??
          null,
        answeredCorrect:
          entry.answeredCorrect ?? entry.AnsweredCorrect ?? false,
        lastAttemptTime: entry.lastAttemptTime ?? entry.LastAttemptTime ?? null,
        TaskContentId: entry.TaskContentId ?? entry.ContentId ?? null,
      };
    });

    setSelectedPlayer(player);
    setItemAttempts(normalizedAttempts);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedPlayer(null);
    setItemAttempts([]);
  };
  const openPreview = (itemcid) => {
    setPreviewItemCid(itemcid);
    //setPreviewOpen(true);
  };

  // const closePreview = () => {
  //   setPreviewOpen(false);
  //   setPreviewItemCid(null);
  // };

  const ItemDetailsDrawer = () => {
    console.log(selectedItemDetails);
    const question = selectedItemDetails?.question;

    // 🧩 Normalize Options to always behave like an array
    const normalizedOptions = Array.isArray(question?.Options)
      ? question.Options
      : question?.Options
      ? Object.values(question.Options).flat()
      : [];

    // 🧠 Render by Question Type
    const renderQuestionContent = () => {
      if (!question) return null;

      switch (question.Type) {
        // 🟦 MATCH
        case "Match":
          return (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              {normalizedOptions.map((opt, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(59,130,246,0.06)",
                    border: "1px solid rgba(59,130,246,0.15)",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: fontStack,
                      fontWeight: 600,
                      color: "primary.main",
                      textAlign: "left",
                    }}
                  >
                    {opt.Name}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: fontStack,
                      fontWeight: 700,
                      color: "text.primary",
                      textAlign: "right",
                      direction: "rtl",
                    }}
                  >
                    {opt.Value}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        // 🟩 MCMA (supports text & image options)
        case "MCMA":
          return (
            <Box>
              {normalizedOptions.map((opt, i) => {
                const isImage =
                  typeof opt.Value === "string" &&
                  /\.(png|jpg|jpeg|gif|webp)$/i.test(opt.Value);

                return (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: isImage ? "center" : "flex-start",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      p: 1,
                      borderRadius: 1,
                      ...(opt.IsCorrect
                        ? { bgcolor: "rgba(16,185,129,0.1)" }
                        : { bgcolor: "rgba(239,68,68,0.08)" }),
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {isImage ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <img
                          src={opt.Value}
                          alt={`Option ${i + 1}`}
                          style={{
                            width: "80px", // fixed width
                            height: "auto", // keep proportions
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          }}
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: fontStack,
                          color: opt.IsCorrect
                            ? "success.main"
                            : "text.secondary",
                        }}
                      >
                        {opt.Value}
                      </Typography>
                    )}

                    {/* ✅ Visual Correctness Indicator */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: opt.IsCorrect
                          ? "success.main"
                          : "text.secondary",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {opt.IsCorrect ? "Correct Option" : "Incorrect Option"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );

        // 🟨 REARRANGE
        case "ReArrange":
          return (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                p: 1.5,
                borderRadius: 1,
                border: "1px dashed #ccc",
                backgroundColor: "#f8fafc",
                direction: question.Direction === "RTL" ? "rtl" : "ltr",
              }}
            >
              {normalizedOptions.map((opt, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    fontFamily: "Traditional Arabic, serif",
                    fontSize: "1.2rem",
                    bgcolor: "white",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    boxShadow: 1,
                  }}
                >
                  {opt.Value}
                </Typography>
              ))}
            </Box>
          );

        // 🟧 RANKING
        case "Ranking":
          return (
            <Box sx={{ mt: 1.5 }}>
              {normalizedOptions.map((opt, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "rgba(0,0,0,0.03)",
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      fontFamily: fontStack,
                      minWidth: "24px",
                    }}
                  >
                    {idx + 1}.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fontStack,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {opt.Value}
                  </Typography>
                </Box>
              ))}
            </Box>
          );

        // 🟪 MARKUP
        case "Markup": {
          const markupOptions = Object.entries(question.Options || {});
          return (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "primary.main",
                  fontFamily: fontStack,
                }}
              >
                Text:
              </Typography>
              <Typography
                sx={{
                  direction: question.Direction === "RTL" ? "rtl" : "ltr",
                  fontFamily: "Traditional Arabic, serif",
                  lineHeight: 1.8,
                }}
              >
                {question.Text}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Markup Options:
              </Typography>

              {markupOptions.map(([key, arr], idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 0.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(147,197,253,0.1)",
                    border: "1px solid rgba(59,130,246,0.15)",
                  }}
                >
                  {Array.isArray(arr) &&
                    arr.map((opt, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{ fontFamily: fontStack, color: "text.primary" }}
                      >
                        {opt.Type}
                        {opt.Rationale?.Text && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "text.secondary" }}
                          >
                            💡 {opt.Rationale.Text}
                          </Typography>
                        )}
                      </Typography>
                    ))}
                </Box>
              ))}
            </Box>
          );
        }

        // 🟫 MFB
        case "MFB":
          return (
            <Box
              sx={{
                mt: 1.5,
                direction: "rtl",
                fontFamily: "Traditional Arabic, serif",
                fontSize: "1.4rem",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Main Sentence:
              </Typography>
              <Typography sx={{ mb: 1 }}>
                {question.Options?.[0]?.Value}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Correct Dropdown Values:
              </Typography>
              {question.Options?.[0]?.Value?.split(" ")
                .filter((chunk) => chunk.includes("/"))
                .map((chunk, i) => (
                  <Typography key={i} sx={{ my: 0.5 }}>
                    {chunk.split("/")[0]}
                  </Typography>
                ))}
            </Box>
          );

        // 🟩 IRAAB
        case "Iraab":
          const harakaPattern = /[ًٌٍَُِْ]$/;
          return (
            <Box sx={{ direction: "rtl", textAlign: "right", mt: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Iraab Words:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {question.Iraab?.Value?.split(" ").map((word, idx) => (
                  <Typography
                    key={idx}
                    sx={{
                      fontFamily: "Traditional Arabic, serif",
                      fontSize: "1.5rem",
                      bgcolor: harakaPattern.test(word)
                        ? "rgba(34,197,94,0.1)"
                        : "rgba(239,68,68,0.08)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {word}
                  </Typography>
                ))}
              </Box>
            </Box>
          );

        default:
          return (
            <Typography variant="body2">Unsupported question type.</Typography>
          );
      }
    };

    return (
      <Drawer
        anchor="right"
        open={itemDrawerOpen}
        onClose={() => setItemDrawerOpen(false)}
        PaperProps={{
          sx: (t) => ({
            width: { xs: "100%", sm: 420 },
            p: 0,
            ...surfaceSx(t),
          }),
        }}
      >
        {selectedItemDetails ? (
          <Box sx={{ p: 2.5 }}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontFamily: fontStack,
                  letterSpacing: ".01em",
                }}
              >
                {selectedItemDetails.title}
              </Typography>
              <IconButton onClick={() => setItemDrawerOpen(false)} size="small">
                <HighlightOffIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Question Title */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, fontFamily: fontStack, mb: 1 }}
              dangerouslySetInnerHTML={{ __html: question?.Title || "" }}
            />

            {/* Dynamic Renderer */}
            {renderQuestionContent()}

            <Divider sx={{ my: 2 }} />

            {/* Attempts Info */}
            <Typography variant="body2" color="text.secondary">
              Attempts: <strong>{selectedItemDetails.attemptsCount}</strong>{" "}
              {" · "} Result:{" "}
              <strong
                style={{
                  color: selectedItemDetails.answeredCorrect
                    ? "#16a34a"
                    : "#dc2626",
                }}
              >
                {selectedItemDetails.answeredCorrect ? "Correct" : "Incorrect"}
              </strong>
            </Typography>

            {selectedItemDetails.lastAttempt && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Last Attempt:{" "}
                <Moment format="MM/DD/YYYY hh:mm A">
                  {selectedItemDetails.lastAttempt}
                </Moment>
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography>No question selected.</Typography>
          </Box>
        )}
      </Drawer>
    );
  };

  // Details Drawer (inline component for clarity)
  const DetailsDrawer = () => {
    if (!selectedPlayer) return null;

    // --- Helper to normalize absurdly small avgResponseMs values ---
    const normalizeMs = (value) => {
      let ms = Number(value);
      if (!Number.isFinite(ms) || ms <= 0) return null;

      // Auto-scale for scientific notation edge cases
      if (ms < 1e-9) ms *= 1e12;
      else if (ms < 1e-6) ms *= 1e9;
      else if (ms < 1e-3) ms *= 1e6;
      else if (ms < 1) ms *= 1e3;

      // Clip huge numbers (safety: max 1h)
      if (ms > 3.6e6) ms = 3.6e6;
      return ms;
    };

    const summary = selectedPlayer.ActivitySummary || {};
    console.log(selectedPlayer);
    console.log("summary", summary);
    console.log(itemAttempts);

    // ✅ total items count (fallback to questionCount)
    const totalForPlayer =
      selectedPlayer.ItemCount ??
      selectedPlayer.ItemsTotal ??
      selectedPlayer.TotalItems ??
      questionCount ??
      0;

    // ✅ Items attended → use Score if ItemsCompleted not sent
    const itemsAttended =
      summary.itemsAttended ??
      coerceNumber(selectedPlayer.Score ?? selectedPlayer.Points) ??
      selectedPlayer.ItemsCompleted ??
      0;

    // ✅ Total attempts → try multiple sources, finally count ActivityAttempts
    const totalAttempts =
      summary.totalAttempts ??
      selectedPlayer.TotalAttempts ??
      (Array.isArray(itemAttempts)
        ? itemAttempts.reduce(
            (sum, attempt) => sum + (Number(attempt.attempts) || 0),
            0
          )
        : 0);

    // ✅ Correct answers → prefer summary, then Score/Points
    const correctCount =
      summary.correctCount ??
      coerceNumber(selectedPlayer.Score ?? selectedPlayer.Points) ??
      0;

    // ✅ Compute average response time across all attempts
    let averageResponse = "—";
    if (Array.isArray(itemAttempts) && itemAttempts.length > 0) {
      const validTimes = itemAttempts
        .map((a) => normalizeMs(a.avgResponseMs))
        .filter((t) => Number.isFinite(t) && t > 0);

      if (validTimes.length > 0) {
        const avgMs =
          validTimes.reduce((sum, t) => sum + t, 0) / validTimes.length;
        averageResponse =
          avgMs >= 1000
            ? `${(avgMs / 1000).toFixed(1)}s`
            : `${avgMs.toFixed(0)} ms`;
      }
    }

    // ✅ Last seen
    const lastSeen = summary.lastAttemptTime ?? selectedPlayer.LastReportedOn;

    return (
      <Drawer
        anchor="right"
        open={detailsOpen}
        onClose={closeDetails}
        PaperProps={{
          sx: (t) => ({
            width: { xs: "100%", sm: 460 },
            p: 0,
            ...surfaceSx(t),
          }),
        }}
      >
        {/* Header */}
        <Box
          sx={(t) => ({
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom:
              t.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(15,23,42,0.08)",
            ...gradientCardSx(t),
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar>{initials(selectedPlayer)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 800,
                  letterSpacing: ".01em",
                  fontFamily: fontStack,
                }}
              >
                {(selectedPlayer.FirstName || selectedPlayer.Name || "Player") +
                  " " +
                  (selectedPlayer.LastName || "")}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedPlayer.TeamName ||
                  selectedPlayer.Email ||
                  "Solo player"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeDetails} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2.5 }}>
          <StatusChip player={selectedPlayer} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 1.25,
              mt: 2,
            }}
          >
            <Stat
              label="Items attended"
              value={`${itemsAttended}${
                totalForPlayer ? ` / ${totalForPlayer}` : ""
              }`}
            />
            <Stat label="Correct answers" value={String(correctCount)} />
            <Stat label="Total attempts" value={String(totalAttempts)} />
            <Stat label="Avg response" value={averageResponse || "—"} />
            <Stat
              label="Last active"
              value={
                lastSeen ? (
                  <Typography variant="body2" component="span">
                    <Moment fromNow>{lastSeen}</Moment>
                  </Typography>
                ) : (
                  "—"
                )
              }
            />
          </Box>

          <Divider sx={{ my: 2.25 }} />

          {/* Attempts */}
          <Typography
            variant="overline"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 700,
              letterSpacing: ".08em",
              fontFamily: fontStack,
            }}
          >
            <VisibilityIcon fontSize="small" />
            Attempts
          </Typography>

          {itemAttempts.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                mt: 1,
              }}
            >
              {itemAttempts.map((attempt, index) => {
                const key = attempt.itemKey ?? attempt.itemId ?? index;
                const attemptTitle = attempt.itemId
                  ? `Item ${attempt.itemId}`
                  : `Attempt ${index + 1}`;
                const attemptsCount = attempt.attempts ?? 0;

                // ✅ Normalize avgResponseMs
                let avgMs = normalizeMs(coerceNumber(attempt.avgResponseMs));
                console.log("🧩 normalized avgMs:", avgMs);

                let avgLabel = null;
                if (Number.isFinite(avgMs) && avgMs > 0) {
                  if (avgMs < 1) {
                    // absurdly tiny → show "< 1 ms"
                    avgLabel = "< 1 ms";
                  } else if (avgMs >= 1000) {
                    avgLabel = `${(avgMs / 1000).toFixed(1)}s`;
                  } else {
                    avgLabel = `${avgMs.toFixed(0)} ms`;
                  }
                }

                const answeredCorrect = attempt.answeredCorrect;
                const lastAttempt = attempt.lastAttemptTime ?? null;

                const question =
                  quizData?.Activities?.find(
                    (q) =>
                      String(q.ContentId) === String(attempt.itemId) ||
                      String(q.ContentId) === String(attempt.TaskContentId)
                  ) || null;

                return (
                  <Box
                    key={`${key}-${index}`}
                    sx={(t) => ({
                      p: 1.25,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "transform .15s ease, box-shadow .15s ease",
                      cursor: "pointer",
                      ...(t.palette.mode === "dark"
                        ? { bgcolor: "rgba(255,255,255,0.05)" }
                        : { bgcolor: "rgba(59,130,246,0.08)" }),
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: glowShadow(t),
                      },
                    })}
                    onClick={() => {
                      if (question) {
                        setSelectedItemDetails({
                          title: attemptTitle,
                          question,
                          attemptsCount,
                          answeredCorrect,
                          lastAttempt,
                        });
                        setItemDrawerOpen(true);
                      }
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 700, fontFamily: fontStack }}
                      >
                        {attemptTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Attempts: {attemptsCount} ·{" "}
                        {answeredCorrect ? "Correct" : "Incorrect"}
                        {avgLabel ? ` · Avg ${avgLabel}` : ""}
                        {lastAttempt ? (
                          <>
                            {" · Last "}
                            <Moment format="MM/DD/YYYY hh:mm A">
                              {lastAttempt}
                            </Moment>
                          </>
                        ) : null}
                      </Typography>
                    </Box>

                    {attempt.TaskContentId && (
                      <Tooltip title="Preview question">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPreview(attempt.TaskContentId);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: answeredCorrect
                          ? "success.main"
                          : "error.main",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No attempts yet.
            </Typography>
          )}
        </Box>
      </Drawer>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        "&, & *": { fontFamily: fontStack },
      }}
    >
      <Button
        onClick={() => navigate("/coursepage")}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          color: "primary.main",
          textDecoration: "underline",
          textDecorationColor: "#424242",
        }}
      >
        My Courses
      </Button>
      {/* Header */}
      <Card
        elevation={0}
        sx={(t) => ({
          p: 2.25,
          mb: 3,
          borderRadius: 2,
          ...surfaceSx(t),
          ...gradientCardSx(t),
          boxShadow: glowShadow(t),
        })}
      >
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 700,
                letterSpacing: ".08em",
              }}
            >
              <GroupIcon fontSize="small" /> {gamename}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 900, letterSpacing: ".01em", lineHeight: 1.15 }}
            >
              {modulename}
            </Typography>
            <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
              <Chip
                icon={<BoltIcon sx={{ color: "teal.main" }} />}
                label={`${players.length} participants`}
                sx={{ mr: 1, ...chipTextSx }}
                variant="outlined"
              />
              {typeof questionCount === "number" && (
                <Chip label={`${questionCount} questions`} sx={chipTextSx} />
              )}
            </Box>
          </Box>

          {/* Timer */}
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              minWidth: 140,
              textAlign: "center",
              gap: 1,
            }}
          >
            {timesUp ? (
              <Typography
                variant="subtitle1"
                sx={{
                  color: "warning.main",
                  fontWeight: 800,
                  letterSpacing: ".04em",
                }}
              >
                Time’s Up!
              </Typography>
            ) : (
              <TimerRing
                startedon={startedon}
                duration={duration}
                onTimesUp={() => setTimesUp(true)}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Started <Moment fromNow>{startedon}</Moment>
            </Typography>
          </Box>

          <Box textAlign="right" sx={{ minWidth: 160 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: ".08em" }}
            >
              Game PIN
            </Typography>
            <Typography
              sx={{
                letterSpacing: "0.22em",
                fontWeight: 900,
                fontSize: "1.25rem",
                lineHeight: 1.15,
              }}
            >
              {gamecode}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Participants */}
      <Card
        elevation={0}
        sx={(t) => ({
          borderRadius: 2,
          ...surfaceSx(t),
          p: 0,
          overflow: "hidden",
        })}
      >
        <CardHeader
          sx={(t) => ({
            pb: 0,
            borderBottom:
              t.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(148,163,184,0.28)",
            "& .MuiCardHeader-content": { overflow: "hidden" },
          })}
          title={
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Participants
            </Typography>
          }
          subheader="Review player progress and open detailed attempts from any row."
          action={
            <Chip
              label={`${players.length} joined`}
              variant="outlined"
              sx={chipTextSx}
            />
          }
        />
        <CardContent>
          {isSmall ? (
            <Grid container spacing={2}>
              {sortedPlayers.length > 0 ? (
                sortedPlayers.map((p, i) => {
                  const progress = Math.min(getProgressValue(p), 100);
                  const items = itemsSummary(p);
                  const progLabel = formatProgressLabel(p);
                  const avg = getAvgTime(p);
                  return (
                    <Grid item xs={12} key={`card-${i}`}>
                      <Card
                        variant="outlined"
                        sx={(t) => ({
                          borderRadius: 2,
                          p: 2,
                          cursor: "pointer",
                          transition:
                            "transform .15s ease, box-shadow .15s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: glowShadow(t),
                          },
                          ...(t.palette.mode === "dark"
                            ? { bgcolor: "rgba(255,255,255,0.04)" }
                            : {}),
                        })}
                        onClick={() => openDetails(p)}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar>{initials(p)}</Avatar>
                          <Box flex={1} minWidth={0}>
                            <Typography fontWeight={800} noWrap>
                              {(p.FirstName || p.Name || "Player") +
                                " " +
                                (p.LastName || "")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {p.TeamName || p.Email || "Solo player"}
                            </Typography>
                          </Box>
                          <StatusChip player={p} />
                        </Box>

                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {items === "—"
                                ? "No progress yet"
                                : `${items} items`}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {progLabel}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 3, mt: 0.75 }}
                          />
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Score
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {coerceNumber(p.Score ?? p.Points) ?? "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {avg}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last Active
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {lastActive(p) ? (
                                <Moment fromNow>{lastActive(p)}</Moment>
                              ) : (
                                "—"
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  );
                })
              ) : (
                <Grid item xs={12}>
                  <EmptyState />
                </Grid>
              )}
            </Grid>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={(t) => ({
                borderRadius: 2,
                overflow: "hidden",
                ...surfaceSx(t),
              })}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Participant</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Progress</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Score</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Avg Time</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Last Active</TableCell>
                    {isInstructor && (
                      <TableCell align="right">Action</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPlayers.length > 0 ? (
                    sortedPlayers.map((p, i) => {
                      const progress = Math.min(getProgressValue(p), 100);
                      const items = itemsSummary(p);
                      const progLabel = formatProgressLabel(p);
                      const avg = getAvgTime(p);
                      console.log(progress);
                      return (
                        <TableRow
                          key={`row-${i}`}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => openDetails(p)}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                              }}
                            >
                              <Avatar>{initials(p)}</Avatar>
                              <Box minWidth={0}>
                                <Typography noWrap sx={{ fontWeight: 800 }}>
                                  {(p.FirstName || p.Name || "Player") +
                                    " " +
                                    (p.LastName || "")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {p.TeamName || p.Email || "Solo player"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Joined{" "}
                                  {p.JoinedOn ? (
                                    <Moment fromNow>{p.JoinedOn}</Moment>
                                  ) : (
                                    "—"
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <StatusChip player={p} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {items === "—"
                                ? "No progress yet"
                                : `${items} items`}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{ height: 6, borderRadius: 3, mt: 0.75 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {progLabel}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {coerceNumber(p.Score ?? p.Points) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {avg}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {lastActive(p) ? (
                                <Moment fromNow>{lastActive(p)}</Moment>
                              ) : (
                                "—"
                              )}
                            </Typography>
                          </TableCell>
                          {isInstructor && (
                            <TableCell
                              align="right"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Link
                                component="button"
                                onClick={() => sendRemoveStudent(p)}
                                sx={{ mr: 2, fontWeight: 700 }}
                              >
                                Remove
                              </Link>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isInstructor ? 7 : 6}>
                        <EmptyState />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            {isSmall
              ? "Tap a participant card to view their question-level attempts."
              : "Click a participant row to view their question-level attempts and timings."}
          </Typography>
        </CardContent>
      </Card>

      {/* Leaderboard + Waiting + Offline */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Leaderboard */}
        <Grid item xs={12} md={6} lg={5}>
          <Card sx={(t) => ({ p: 2, borderRadius: 2, ...surfaceSx(t) })}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={(t) => ({
                    bgcolor:
                      t.palette.mode === "dark"
                        ? "rgba(20,184,166,0.2)"
                        : "rgba(20,184,166,0.18)",
                    color: t.palette.mode === "dark" ? "#5eead4" : "#0f766e",
                  })}
                >
                  <EmojiEventsIcon />
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      fontWeight: 800,
                      letterSpacing: ".08em",
                    }}
                  >
                    Leaderboard
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{ fontWeight: 700 }}
                  >
                    {leaderboardPlayers.length > 0
                      ? `${activePlayers.length} players competing`
                      : "No active players yet"}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                {leaderboardPlayers.length > 0 ? (
                  leaderboardPlayers.map((p, idx) => {
                    const progress = Math.min(getProgressValue(p), 100);
                    return (
                      <Box
                        key={`leader-${idx}`}
                        sx={(t) => ({
                          p: 1.25,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          cursor: "pointer",
                          transition:
                            "transform .15s ease, box-shadow .15s ease",
                          ...(t.palette.mode === "dark"
                            ? { bgcolor: "rgba(255,255,255,0.05)" }
                            : { bgcolor: "rgba(59,130,246,0.08)" }),
                          ...(idx === 0
                            ? t.palette.mode === "dark"
                              ? {
                                  background:
                                    "linear-gradient(135deg, rgba(99,102,241,0.24), rgba(45,212,191,0.16))",
                                  boxShadow: glowShadow(t),
                                }
                              : {
                                  background:
                                    "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(45,212,191,0.12))",
                                  boxShadow: glowShadow(t),
                                }
                            : {}),
                          "&:hover": { transform: "translateY(-2px)" },
                        })}
                        onClick={() => openDetails(p)}
                      >
                        <Box
                          sx={(t) => ({
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 900,
                            bgcolor:
                              t.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(15,23,42,0.06)",
                            color:
                              t.palette.mode === "dark" ? "#a5b4fc" : "#312e81",
                            textShadow: "0 1px 2px rgba(0,0,0,0.15)",
                          })}
                        >
                          #{idx + 1}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontWeight: 800 }}
                          >
                            {(p.FirstName || p.Name || "Player") +
                              " " +
                              (p.LastName || "")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {p.TeamName || p.Email || "Solo player"}
                          </Typography>
                        </Box>
                        <Box sx={{ width: 140 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            align="right"
                          >
                            {formatProgressLabel(p)}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Waiting for players to start playing.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Waiting Room + Offline */}
        <Grid item xs={12} md={6} lg={7}>
          <Card sx={(t) => ({ p: 2, borderRadius: 2, ...surfaceSx(t) })}>
            {/* Waiting */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: 800,
                  letterSpacing: ".08em",
                }}
              >
                <Box sx={iconBadgeSx}>
                  <PanToolIcon fontSize="small" />
                </Box>
                Waiting Room
              </Typography>
              <Chip
                label={`${waitingPlayers.length} pending`}
                sx={chipTextSx}
              />
            </Box>

            {waitingPlayers.length === 0 ? (
              <EmptyState title="No players waiting to join." />
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={(t) => ({
                  borderRadius: 2,
                  maxHeight: 360,
                  boxShadow: "none",
                  ...(t.palette.mode === "dark"
                    ? { bgcolor: "transparent" }
                    : {}),
                })}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell align="center">Progress</TableCell>
                      <TableCell align="center">Joined</TableCell>
                      {isInstructor && (
                        <TableCell align="right">Action</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {waitingPlayers.map((p, i) => (
                      <TableRow
                        key={`waiting-${i}`}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => openDetails(p)}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            <Avatar>{initials(p)}</Avatar>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{ fontWeight: 800 }}
                              >
                                {(p.FirstName || p.Name || "Player") +
                                  " " +
                                  (p.LastName || "")}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {p.TeamName || p.Email || "Ready to jump in"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatProgressLabel(p)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary">
                            {p.JoinedOn ? (
                              <Moment fromNow>{p.JoinedOn}</Moment>
                            ) : (
                              "—"
                            )}
                          </Typography>
                        </TableCell>
                        {isInstructor && (
                          <TableCell
                            align="right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="Admit player">
                              <Link
                                component="button"
                                onClick={() => sendAdmitStudent(p)}
                                sx={{ fontWeight: 700 }}
                              >
                                Admit
                              </Link>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Offline */}
            {offlinePlayers.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    fontWeight: 800,
                    letterSpacing: ".08em",
                  }}
                >
                  <Box sx={iconBadgeSx}>
                    <PowerSettingsNewIcon fontSize="small" />
                  </Box>
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {offlinePlayers.map((p, i) => (
                    <Box
                      key={`offline-${i}`}
                      onClick={() => openDetails(p)}
                      sx={(t) => ({
                        p: 1.25,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        ...(t.palette.mode === "dark"
                          ? { bgcolor: "rgba(255,255,255,0.05)" }
                          : { bgcolor: "rgba(148,163,184,0.18)" }),
                        "&:hover": {
                          ...(t.palette.mode === "dark"
                            ? { bgcolor: "rgba(255,255,255,0.08)" }
                            : { bgcolor: "rgba(148,163,184,0.26)" }),
                        },
                      })}
                    >
                      <Typography fontWeight={800} noWrap>
                        {(p.FirstName || p.Name || "Player") +
                          " " +
                          (p.LastName || "")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatProgressLabel(p)} · Last active{" "}
                        {lastActive(p) ? (
                          <Moment fromNow>{lastActive(p)}</Moment>
                        ) : (
                          "—"
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Details Drawer */}
      <DetailsDrawer />
      <ItemDetailsDrawer />
    </Box>
  );
}
